import { Facebook, Instagram, MapPin } from "lucide-react"
import { Link } from "react-router-dom"
import { useWebsiteConfig } from "@/hooks/useWebsiteConfig"

const externalUrl = (value: string | null | undefined, httpsOnly = false) => {
  if (!value) return null
  try {
    const url = new URL(value)
    return url.protocol === "https:" || (!httpsOnly && url.protocol === "http:")
      ? url.href
      : null
  } catch {
    return null
  }
}

export function WebsiteFooter() {
  const { data: config } = useWebsiteConfig()
  const title = config?.title?.trim() || "SHOP.CO"
  const mapUrl = externalUrl(config?.map_embed_url, true)
  const socials = [
    {
      name: "Facebook",
      url: externalUrl(config?.facebook_url),
      icon: <Facebook size={19} />,
    },
    {
      name: "Instagram",
      url: externalUrl(config?.instagram_url),
      icon: <Instagram size={19} />,
    },
    {
      name: "TikTok",
      url: externalUrl(config?.tiktok_url),
      icon: (
        <svg
          viewBox="0 0 24 24"
          fill="currentColor"
          width="19"
          height="19"
          aria-hidden="true"
        >
          <path d="M16.6 2c.3 2.6 1.8 4.2 4.4 4.4v3.3a8.1 8.1 0 0 1-4.4-1.4v7.1a6.6 6.6 0 1 1-5.7-6.5v3.4a3.3 3.3 0 1 0 2.4 3.1V2h3.3Z" />
        </svg>
      ),
    },
    {
      name: "X",
      url: externalUrl(config?.x_url),
      icon: (
        <svg
          viewBox="0 0 24 24"
          fill="currentColor"
          width="19"
          height="19"
          aria-hidden="true"
        >
          <path d="M18.9 2H22l-6.8 7.8L23.2 22h-6.3L12 14.6 5.5 22H2.3l8.2-9.4L2.8 2h6.4l4.4 6.8L18.9 2Zm-1.1 18h1.7L8.2 4H6.4l11.4 16Z" />
        </svg>
      ),
    },
  ].filter((social) => social.url)

  return (
    <footer
      className="border-t border-indigo-100 bg-indigo-50/70"
      aria-label="Store information"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div
          className={`grid gap-10 py-10 sm:py-12 ${mapUrl ? "lg:grid-cols-[1fr_1fr] lg:gap-16" : "sm:grid-cols-[2fr_1fr]"}`}
        >
          <div className="min-w-0">
            <Link
              to="/"
              className="inline-flex max-w-full items-center gap-3 rounded text-indigo-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-indigo-600"
            >
              {config?.logo_image && (
                <img
                  src={config.logo_image}
                  alt=""
                  className="h-12 max-w-24 object-contain"
                />
              )}
              <span className="min-w-0 break-words text-2xl font-semibold tracking-tight">
                {title}
              </span>
            </Link>
            {config?.footer_description?.trim() && (
              <p className="mt-5 max-w-lg whitespace-pre-line break-words text-sm leading-7 text-gray-600">
                {config.footer_description}
              </p>
            )}
            {socials.length > 0 && (
              <div className="mt-6">
                <h2 className="text-sm font-semibold text-indigo-900">
                  Stay connected
                </h2>
                <div className="mt-3 flex flex-wrap gap-2">
                  {socials.map((social) => (
                    <a
                      key={social.name}
                      href={social.url!}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`Visit ${title} on ${social.name}`}
                      className="inline-flex items-center gap-2 rounded-md border border-indigo-100 bg-white px-3 py-2 text-sm text-indigo-800 hover:border-indigo-300 hover:bg-indigo-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                    >
                      <span aria-hidden="true">{social.icon}</span>
                      {social.name}
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
          {mapUrl && (
            <div className="min-w-0">
              <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold text-indigo-900">
                <MapPin size={18} aria-hidden="true" />
                Find our store
              </h2>
              <iframe
                src={mapUrl}
                title={`${title} store location`}
                loading="lazy"
                referrerPolicy="strict-origin-when-cross-origin"
                sandbox="allow-scripts allow-same-origin allow-popups"
                allowFullScreen
                className="h-60 w-full rounded-lg border border-indigo-100 bg-white"
              />
            </div>
          )}
        </div>
        <div className="break-words border-t border-indigo-100 py-5 text-xs text-gray-600">
          © {new Date().getFullYear()} {title}. All rights reserved.
        </div>
      </div>
    </footer>
  )
}
